import { Directive, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription, forkJoin } from 'rxjs';
// COMPONENTS
import { MultipleAnswerComponent } from '@shared/components/answer/multiple-answer/multiple-answer.component';
import { OpenAnswerComponent } from '@shared/components/answer/open-answer/open-answer.component';
import { RelationalAnswerComponent } from '@shared/components/answer/relational-answer/relational-answer.component';
import { TrueFalseAnswerComponent } from '@shared/components/answer/true-false-answer/true-false-answer.component';
import { ImageMultipleAnswerComponent } from '@shared/components/answer/image-multiple-answer/image-multiple-answer.component';
// SERVICES
import { QuestionService } from '@services/question/question.service';
import { AlertService } from '@services/notification/alert.service';
import { AnswerService } from '@services/answer/answer.service';
// MODELS
import { QuestionBank } from '@models/question-bank/QuestionBank';
import { Question } from '@models/question/Question';
import { Response } from '@models/response-api/ResponseApi';
import { QUESTION_TYPE } from '@constants/QuestionType';
// TODO : TAG GTM
declare var dataLayer: any;
@Directive({ selector: 'reagent-base' })
export abstract class ReagentBase implements OnInit {
	subscription = new Subscription();
	QUESTION_TYPE = QUESTION_TYPE;
	question?: Question;
	questionDraft?: Question;
	questionBank: QuestionBank;
	@ViewChild(MultipleAnswerComponent) multipleAnswerComponent?: MultipleAnswerComponent;
	@ViewChild(OpenAnswerComponent) openAnswerComponent?: OpenAnswerComponent;
	@ViewChild(RelationalAnswerComponent)
	relationalAnswerComponent?: RelationalAnswerComponent;
	@ViewChild(TrueFalseAnswerComponent) trueFalseAnswerComponent?: TrueFalseAnswerComponent;
	@ViewChild(ImageMultipleAnswerComponent)
	imageMultipleAnswerComponent?: ImageMultipleAnswerComponent;

	cct: string | null = '';
	typeSchool: string | null = '';

	constructor(
		protected route: ActivatedRoute,
		private questionService: QuestionService,
		private answerService: AnswerService,
		private alertService: AlertService
	) {
		this.question = this.route.snapshot.data['data'] as Question;
		this.questionBank = this.route.parent?.snapshot.data['data'] as QuestionBank;
		this.questionDraft = this.question;
	}

	ngOnInit() {
		this.cct = localStorage.getItem('cct');
		this.typeSchool = localStorage.getItem('typeSchool');
	}

	showImageMultipleAnswer() {
		return this.question && this.question.questionTypeId == QUESTION_TYPE.IMAGE_MULTI;
	}

	showMultipleAnswer() {
		return this.question && this.question.questionTypeId == QUESTION_TYPE.MULTI;
	}

	showOpenAnswer() {
		return this.question && this.question.questionTypeId == QUESTION_TYPE.OPEN;
	}

	showRelationalAnswer() {
		return this.question && this.question.questionTypeId == QUESTION_TYPE.RELATIONAL;
	}

	showTrueFalseAnswer() {
		return this.question && this.question.questionTypeId == QUESTION_TYPE.TRUE_AND_FALSE;
	}

	existChangeTypeQuestion() {
		return this.question?.questionTypeId != this.questionDraft?.questionTypeId;
	}

	save(question: Question) {
		this.subscription.add(
			this.questionService.save(question).subscribe({
				next: (x: Response) => {
					const { success, data, message } = x;
					if (success) {
						this.alertService.showSmallSuccess(message);
						this.navigateQuestion(data);
					} else {
						dataLayer.push({
							event: 'error_message', //Static data
							content_group: 'Banco de preguntas', //Dynamic data
							error_messgae_text: message, //Dynamic data
							error_messgae_location: 'Guardar pregunta', //Dynamic data
							cct: this.cct, //Dynamic data
							school_type: this.typeSchool,
						});
						this.alertService.showError(message);
					}
				},
				error: (error: HttpErrorResponse) => {
					console.log(error);
					this.alertService.showSmallErrorServer();
				},
			})
		);
	}

	getAnswers() {
		if (this.showMultipleAnswer()) {
			return this.multipleAnswerComponent?.formAnswers.value;
		} else if (this.showOpenAnswer()) {
			return this.openAnswerComponent?.formAnswers.value;
		} else if (this.showRelationalAnswer()) {
			return this.relationalAnswerComponent?.formAnswers.value;
		} else if (this.showTrueFalseAnswer()) {
			return this.trueFalseAnswerComponent?.formAnswers.value;
		} else if (this.showImageMultipleAnswer()) {
			return this.imageMultipleAnswerComponent?.formAnswers.value;
		} else {
			return [];
		}
	}

	async updateQuestion(question: Question) {
		const answers = this.getAnswers();
		question.answers = answers;
		if (this.existChangeTypeQuestion() && question.answers?.length) {
			if (
				await this.alertService.showConfirmationDeletion({
					text: '¿Al cambiar el tipo de pregunta se eliminaran las respuestas registradas?',
				})
			) {
				this.deleteRange(question);
			}
		} else {
			this.update(question);
		}
	}

	update(question: Question) {
		this.subscription.add(
			this.questionService.update(question).subscribe({
				next: (x: Response) => {
					const { success, message } = x;
					if (success) {
						this.question = question;
						this.alertService.showSmallSuccess(message);
					} else {
						dataLayer.push({
							event: 'error_message', //Static data
							content_group: 'Banco de preguntas', //Dynamic data
							error_messgae_text: message, //Dynamic data
							error_messgae_location: 'Actualizar pregunta', //Dynamic data
							cct: this.cct, //Dynamic data
							school_type: this.typeSchool,
						});
						this.alertService.showError(message);
					}
				},
				error: (error: HttpErrorResponse) => {
					console.log(error);
					this.alertService.showSmallErrorServer();
				},
			})
		);
	}

	deleteRange(question: Question) {
		const $answers = (question.answers || []).map((x) => this.answerService.remove(x));
		this.subscription.add(
			forkJoin([...$answers]).subscribe({
				next: (x: Response[]) => {
					question.answers = [];
					this.update(question);
				},
			})
		);
	}

	abstract navigateQuestion(question: Question): void;
}
